import { AttendanceGuage, Button } from '@wot/components';
import { ITrialClass } from '@wot/typings';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import plansApi from '../../../../api/plans';
import { ADD_CLASSES_ICON, CAUTION, CLOSE_ICON, DEFAULT_PROFILE_PIC, PRO } from '../../../../assets/icons';
import { DropDownItem, Dropdown } from '../../../../components/DropDown';
import LoadingContent from '../../../../components/LoadingContent';
import ModalContainer, { ConfirmationBox, Modal } from '../../../../components/Modal';
import StatusChip from '../../../../components/StatusChip';
import TutorCard from '../../../../components/TutorCard';
import { LPlan } from '../../../../interfaces/Plan';
import xhr from '../../../../utils/fetch';
import { capitalizeFirstLetter, getAttendacePercentage } from '../../../../utils/functions';

interface Props {
  plan: LPlan;
  setShowPopup: any;
  setPlan: React.Dispatch<React.SetStateAction<LPlan | undefined>>;
}

export function PlanCard({ plan, setPlan, setShowPopup }: Props) {
  const [sessions, setSessions] = useState<ITrialClass[]>([]);
  const [payment, setPayment] = useState<number>(plan.tutorPayment || 250);
  const [showSuggestedTutors, setShowSuggestedTutors] = useState(false);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    xhr({
      url: `/u/${plan.userId}/plans/${plan._id}/sessions`,
      method: 'GET'
    }).then((res) => {
      if (res.status === 'OK') {
        setSessions(res.data);
      }
    });
  }, []);

  const updateTutorPayment = () => {
    setLoading(true);
    plansApi.updatePlan(plan._id, { tutorPayment: payment }).then(() => {
      setLoading(false);
      toast.success('Tutor Payment updated');
    });
  };

  const updatePlanPaymentStatusToPaid = () => {
    plansApi
      .updatePlanPaymentStatus(plan._id, 'paid')
      .then(() => {
        if (plan) {
          toast.success(`Plan id: ${plan._id} marked as paid`);
          setPlan({
            ...plan,
            paymentDetails: {
              paymentMethod: plan.paymentDetails?.paymentMethod || 'cash',
              status: 'paid'
            }
          });
        }
      })
      .catch(() => {
        toast.error(`Something went wrong.`);
      });
  };

  return (
    <div
      className={`w-full max-w-[1250px] relative flex flex-col md:flex-row rounded-lg border-primary border-l-4 overflow-hidden border`}
    >
      <div className="w-full flex flex-col gap-7 px-7 py-4">
        <div>
          <div className="w-full flex gap-4 items-center">
            <div className="w-max bg-neutralBlue2 p-2 rounded-md">
              <p className="text-sm font-semibold">Plan ID: {plan._id}</p>
            </div>
            <div className="flex h-max gap-2">
              <p className="text-sm font-semibold">Payment Status: </p>
              {plan.paymentDetails?.status === 'pending' ? (
                <Dropdown
                  menu={[
                    {
                      key: 1,
                      label: (
                        <ModalContainer
                          ModalComp={
                            <ConfirmationBox
                              loading={loading}
                              text="Confirm mark this plan as paid?"
                              onConfirm={() => {
                                updatePlanPaymentStatusToPaid();
                              }}
                            />
                          }
                        >
                          <DropDownItem label="Paid" />
                        </ModalContainer>
                      )
                    }
                  ]}
                  placement="bottomLeft"
                >
                  <StatusChip
                    expand={plan.status === 'active' || plan.status === 'pending'}
                    state={
                      plan.paymentDetails?.status === 'pending'
                        ? 'warning'
                        : plan.paymentDetails?.status === 'paid'
                          ? 'success'
                          : plan.paymentDetails?.status === 'failed'
                            ? 'error'
                            : 'pending'
                    }
                  >
                    <p className="font-semibold text-xs w-24">{plan.paymentDetails?.status.toUpperCase()}</p>
                  </StatusChip>
                </Dropdown>
              ) : (
                <StatusChip
                  expand={false}
                  state={
                    plan.paymentDetails?.status === 'paid'
                      ? 'success'
                      : plan.paymentDetails?.status === 'failed'
                        ? 'error'
                        : 'pending'
                  }
                >
                  <p className="font-semibold text-xs w-24">{plan.paymentDetails?.status.toUpperCase()}</p>
                </StatusChip>
              )}
            </div>
          </div>
          <div className="w-max py-1 flex items-center gap-2">
            <p className="text-sm text-neutralGray1">Plan Status: </p>
            <span className="rounded-full border text-xs font-medium px-3 py-1">{plan.status}</span>
          </div>
          <div className="w-max py-1 flex items-center gap-2">
            <p className="text-sm text-neutralGray1">Plan Category: </p>
            <span className="text-xs font-medium py-1">{capitalizeFirstLetter(plan.categoryType)}</span>
          </div>
          <div className="w-max py-1 flex items-center gap-2">
            <p className="text-sm text-neutralGray1">{`Plan ${plan.categoryType === 'subject' ? 'Subjects' : capitalizeFirstLetter(plan.categoryType)
              }:`}</p>
            <span className="text-xs font-medium py-1">{plan.category.name}</span>
          </div>
        </div>
        <div className="w-full flex flex-col md:flex-row md:flex-nowrap gap-7">
          <div className="flex flex-wrap gap-7">
            <div className="flex flex-col gap-2">
              <p className="text-3xl font-bold text-primary">
                {sessions.filter((s) => s.status === 'completed').length}/{plan.totalSessions}
              </p>
              <p className="text-sm text-neutralGray2 font-medium">Completed Classes</p>
            </div>

            {plan.totalSessions - plan.purchasedSessions > 0 ? (
              <div className="flex flex-col gap-2">
                <p className="text-3xl font-bold text-primary">{plan.totalSessions - plan.purchasedSessions}</p>
                <div className="flex gap-2 items-center">
                  <img
                    src={ADD_CLASSES_ICON}
                    className="h-5 cursor-pointer"
                    onClick={() => {
                      plan.status !== 'cancelled' && setShowPopup(true);
                    }}
                  />
                  <p className="text-sm text-neutralGray2 font-medium">Complimentary classes </p>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <img
                  src={ADD_CLASSES_ICON}
                  className="h-9 cursor-pointer"
                  onClick={() => {
                    plan.status !== 'cancelled' && setShowPopup(true);
                  }}
                />
                <p className="text-sm text-neutralGray2 font-medium">Complimentary classes </p>
              </div>
            )}

            <div className="flex flex-col gap-2">
              <p className="text-3xl font-bold text-primary">
                {plan.totalSessions -
                  sessions.filter((s) => s.status === 'completed' || s.status === 'upcoming').length}
              </p>
              <p className="text-sm text-neutralGray2 font-medium">Remaining classes</p>
            </div>

            <div className="flex flex-col gap-2">
              <p className="text-3xl font-bold text-primary">
                {sessions.filter((s) => s.status === 'upcoming').length}
              </p>
              <p className="text-sm text-neutralGray2 font-medium">Upcoming classes</p>
            </div>
            <div className="flex flex-wrap gap-7">
              <div className="flex flex-col gap-2">
                <p className="text-3xl font-bold text-primary">INR {plan.totalCost}</p>
                <p className="text-sm text-neutralGray2 font-medium">COST</p>
              </div>

              <div className="flex flex-col gap-2">
                <p className="text-3xl font-bold text-primary">{new Date(plan.createdAt).toLocaleDateString()}</p>
                <p className="text-sm text-neutralGray2 font-medium">Purchase Date</p>
              </div>
            </div>
          </div>
          {plan.tutorId && !plan.prevTutorId && (
            <div className="flex flex-col gap-2">
              <p className="text-base font-semibold text-neutralGray2">ATTENDANCE</p>
              <div className="w-24 h-24">
                <AttendanceGuage
                  percentage={getAttendacePercentage(
                    sessions.filter((i) => i.attendance && i.attendance.student === 'present').length,
                    plan.totalSessions
                  )}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      {plan.changeRequest?.status === 'approved' && !plan.changeRequest.fulfilled ? (
        <div className="flex flex-row">
          <div className="w-full md:w-max flex md:shrink-0">
            <div className="w-full h-full md:w-[300px] flex flex-col border border-neutralGray3 p-4 rounded-lg cursor-pointer bg-neutralBlue2">
              <h1 className="text-sm underline text-center">Previously Assigned Tutor</h1>
              <div className="flex justify-end items-center gap-2">
                {plan.tutor.agreement.level === 'premium' && <img src={PRO} alt="Pro badge" className="h-7 w-7" />}
              </div>
              <div className="h-full flex flex-col gap-7 items-start md:items-center px-8 py-4">
                <div className="flex md:flex-col items-center gap-2">
                  <div className="h-10 w-10 md:h-16 md:w-16 rounded-full overflow-hidden">
                    <img
                      src={plan.tutor.personal.data.profilePic || DEFAULT_PROFILE_PIC}
                      alt="tutor_profile_pic"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <span className="text-2xl font-normal">{plan.tutor.personal.data.name}</span>
                </div>
                {/* <div className="w-full max-w-sm mx-auto mt-auto flex flex-col gap-4 items-center justify-center whitespace-nowrap"> */}
                <div className="flex gap-2 items-center">
                  <p className="text-sm">Tutor’s Payment per Session(INR)</p>
                  <input
                    disabled={plan.status === 'cancelled'}
                    className="w-20 h-8 rounded-md p-2 border"
                    value={payment}
                    onChange={(e) => setPayment(e.target.valueAsNumber)}
                    type="number"
                  />
                </div>
                <div className="w-full max-w-[128px] flex flex-col gap-5">
                  {payment !== plan.tutorPayment && plan.status !== 'cancelled' && (
                    <Button
                      block
                      loading={loading}
                      text="Save"
                      type="s-outlined"
                      onClick={() => {
                        updateTutorPayment();
                      }}
                      size="sm"
                    />
                  )}
                  <Link to={`/tutor-management/manage/${plan.tutorId}`}>
                    <Button block text="View Profile" type="secondary" onClick={() => { }} size="sm" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
          <div className="w-full md:w-max flex md:shrink-0">
            <div className="w-full h-full md:w-[300px] flex flex-col border border-neutralGray3 p-4 rounded-lg cursor-pointer bg-neutralBlue2">
              <h1 className="text-sm underline text-center">Assign New Tutor</h1>
              <div className="w-full h-full grow flex justify-center items-center gap-2">
                <div className="w-full max-w-[200px] flex flex-col gap-5">
                  <Button text="Assign New Tutor" block onClick={() => setShowSuggestedTutors(true)} />
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="w-full md:w-max flex md:shrink-0">
          <div className="w-full h-full md:w-[300px] flex flex-col border border-neutralGray3 p-4 rounded-lg cursor-pointer bg-neutralBlue2">
            <div className="flex justify-end items-center gap-2">
              {plan.tutor.agreement.level === 'premium' && <img src={PRO} alt="Pro badge" className="h-7 w-7" />}
            </div>
            <div className="h-full flex flex-col gap-7 items-start md:items-center px-8 py-4">
              <div className="flex md:flex-col items-center gap-2">
                <div className="h-10 w-10 md:h-16 md:w-16 rounded-full overflow-hidden">
                  <img
                    src={plan.tutor.personal.data.profilePic || DEFAULT_PROFILE_PIC}
                    alt="tutor_profile_pic"
                    className="w-full h-full object-cover"
                  />
                </div>
                <span className="text-2xl font-normal">{plan.tutor.personal.data.name}</span>
              </div>
              <div className="flex gap-2 items-center">
                <p className="text-sm">Tutor’s Payment per Session(INR)</p>
                <input
                  disabled={plan.status === 'cancelled'}
                  className="w-20 h-8 rounded-md p-2 border"
                  value={payment}
                  onChange={(e) => setPayment(e.target.valueAsNumber)}
                  type="number"
                />
              </div>
              <div className="w-full max-w-[128px] flex flex-col gap-5">
                {payment !== plan.tutorPayment && plan.status !== 'cancelled' && (
                  <Button
                    block
                    loading={loading}
                    text="Save"
                    type="s-outlined"
                    onClick={() => {
                      updateTutorPayment();
                    }}
                    size="sm"
                  />
                )}
                <Link to={`/tutor-management/manage/${plan.tutorId}`}>
                  <Button block text="View Profile" type="secondary" onClick={() => { }} size="sm" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {showSuggestedTutors && (
        <AssignNewTutorCard plan={plan} setPlan={setPlan} setShowSuggestedTutors={setShowSuggestedTutors} />
      )}
    </div>
  );
}

function AssignNewTutorCard({
  plan,
  setShowSuggestedTutors
}: {
  plan: LPlan;
  setPlan: any;
  setShowSuggestedTutors: any;
}) {
  const [suggestedTutors, setSuggestedTutors] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  useEffect(() => {
    xhr({
      url: `/u/${plan.userId}/plans/${plan._id}/suggested-tutors`,
      method: 'GET'
    })
      .then((res) => {
        if (res.data && res.data.length > 0) {
          setSuggestedTutors(res.data[0].suggestedTutors.filter((item: any) => item._id !== plan.tutorId));
        }
      })
      .catch((err) => err);
  }, []);

  const assignNewTutor = (id: string) => {
    setLoading(true);
    xhr({
      url: `/u/${plan.userId}/plans/${plan._id}/assign-tutor`,
      method: 'PUT',
      body: {
        tutorId: id
      }
    })
      .then((res) => {
        if (res.data) {
          setShowSuggestedTutors(false);
          toast.success('Successfully assigned a new tutor.');
          navigate(`/plan-management/bookings/${res.data._id}`);
        }
        setLoading(false);
      })
      .catch(() => {
        toast.error('Something went wrong.');
        setLoading(false);
      });
  };

  return (
    <Modal handleClose={() => setShowSuggestedTutors(false)}>
      <div
        id="portal"
        className="w-full max-h-[700px] max-w-3xl flex flex-col gap-4 bg-white p-6 rounded-md overflow-y-scroll custom-scrollbar"
      >
        <div className="flex relative w-full items-center justify-center">
          <h1 className="text-2xl font-semibold text-center">Assign A New Tutor</h1>
          <div className="absolute right-0" onClick={() => setShowSuggestedTutors(false)}>
            <img src={CLOSE_ICON} className="w-4 h-4 cursor-pointer" />
          </div>
        </div>
        {loading ? (
          <>
            <LoadingContent />
          </>
        ) : (
          <>
            {suggestedTutors && suggestedTutors.length > 0 ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:my-2 gap-4">
                  {suggestedTutors?.map((item: any, i) => (
                    <div className="w-full flex flex-col max-w-sm" key={i}>
                      <TutorCard item={item} />
                      <Button block type="primary" text="Assign" onClick={() => assignNewTutor(item._id)} />
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="flex flex-col gap-5 items-center py-10">
                <img src={CAUTION} alt="No tutors found" />
                <div className="flex flex-col items-center w-full gap-2">
                  <span className="text-2xl font-medium">Oops,</span>
                  <span className="container text-sm font-normal text-center">
                    No tutors found for this plan
                  </span>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
